import { apiClient } from "../index";

const ORGANIZATION_OVERVIEW_PATH =
  "/api/v1/dashboard/company-admin/organization-overview";
const MEMBER_ANALYTICS_PATH =
  "/api/v1/dashboard/company-admin/member-analytics";
const PERFORMANCE_METRICS_PATH =
  "/api/v1/dashboard/company-admin/performance-metrics";
const LEARNING_ACTIVITY_PATH =
  "/api/v1/dashboard/company-admin/learning-activity";
const BOX_LIBRARY_PATH = "/api/v1/dashboard/company-admin/box-library";
const TOKEN_USAGE_PATH = "/api/v1/dashboard/company-admin/token-usage";

/**
 * Row 1 — Organization Overview.
 */
export async function getOrganizationOverview() {
  return apiClient.get(ORGANIZATION_OVERVIEW_PATH);
}

/**
 * Normalize the raw payload into the flat shape the overview cards expect.
 * Defensive about missing fields so the UI never crashes on partial data.
 */
export function normalizeOrganizationOverview(raw) {
  // Member growth: pull the first dataset's data; labels stay ISO for the spark
  const growthSeries = raw?.members_growth_chart?.datasets?.[0]?.data ?? [];

  // Teams-size histogram: each bucket label is a member-count, value is #teams
  const teamsSizeLabels = raw?.teams_size_chart?.labels ?? [];
  const teamsSizeValues = raw?.teams_size_chart?.datasets?.[0]?.data ?? [];

  // Approval donut: payload gives raw [Approved, Pending] counts.
  // approvedPct is derived (not in payload).
  const approvalLabels = raw?.boxes_approval_chart?.labels ?? [];
  const approvalValues = raw?.boxes_approval_chart?.datasets?.[0]?.data ?? [];
  const byLabel = (name, fallbackIdx) => {
    const i = approvalLabels.findIndex((l) => String(l).toLowerCase() === name);
    const idx = i !== -1 ? i : fallbackIdx;
    return approvalValues[idx] ?? 0;
  };
  const approved = byLabel("approved", 0);
  const pending = byLabel("pending", 1);
  const approvalDenom = approved + pending;
  const approvedPct =
    approvalDenom > 0 ? Math.round((approved / approvalDenom) * 100) : 0;

  return {
    totalMembers: raw?.total_members ?? 0,
    newMembers30d: raw?.new_members_30d ?? 0,
    totalTeams: raw?.total_teams ?? 0,
    emptyTeams: raw?.empty_teams ?? 0,
    ownedBoxes: raw?.owned_boxes ?? 0,
    pendingApprovals: raw?.pending_approvals ?? 0,

    memberGrowth: growthSeries,

    teamsSize: {
      labels: teamsSizeLabels.map(String),
      values: teamsSizeValues,
    },

    approval: {
      approved,
      pending,
      approvedPct,
    },
  };
}

/**
 * Row 2 — Member Analytics.
 */
export async function getMemberAnalytics() {
  return apiClient.get(MEMBER_ANALYTICS_PATH);
}

/**
 * Normalize member-analytics. ISO labels are formatted to "Mon D" for the
 * x-axis; role counts come from the scalar fields (chart data is a fallback).
 */
export function normalizeMemberAnalytics(raw) {
  const labels = raw?.new_members_7d_chart?.labels ?? [];
  const series = raw?.new_members_7d_chart?.datasets?.[0]?.data ?? [];

  const roleData = raw?.members_by_role_chart?.datasets?.[0]?.data ?? [];

  return {
    newMembers7dTotal: raw?.new_members_7d_total ?? 0,
    labels: labels.map((iso) => {
      const d = new Date(iso + "T00:00:00"); // avoid TZ shifting the date
      return d.toLocaleDateString("en", { month: "short", day: "numeric" });
    }),
    series,
    roles: {
      users: raw?.company_users ?? roleData[0] ?? 0,
      admins: raw?.company_admins ?? roleData[1] ?? 0,
    },
  };
}

/** Build up-to-2-char initials from an org name. */
function initialsFromName(name) {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Row 3 — Performance Metrics.
 */
export async function getPerformanceMetrics(
  topTeamsLimit = 5,
  topPerformersLimit = 5,
) {
  return apiClient.get(
    `${PERFORMANCE_METRICS_PATH}?top_teams_limit=${topTeamsLimit}&top_performers_limit=${topPerformersLimit}`,
  );
}

/**
 * Normalize performance-metrics. Performer initials/tints aren't in the
 * payload, so initials are derived from display_name and tint cycles a
 * fixed palette by index.
 */
export function normalizePerformanceMetrics(raw) {
  const TINT_KEYS = ["primary", "blue", "violet", "orange", "teal"];

  const teamLabels = raw?.top_teams_chart?.labels ?? [];
  const teamValues = raw?.top_teams_chart?.datasets?.[0]?.data ?? [];

  const performers = raw?.top_performers ?? [];

  return {
    topTeams: {
      labels: teamLabels,
      values: teamValues,
    },
    topPerformers: performers.map((p, i) => ({
      id: p.user_id ?? `${p.display_name ?? "user"}-${i}`,
      name: p.display_name ?? "Unknown",
      team: p.team_name ?? "—",
      attempts: p.attempted ?? 0,
      completed: p.completed ?? 0,
      initials: initialsFromName(p.display_name),
      tint: TINT_KEYS[i % TINT_KEYS.length],
    })),
  };
}

/**
 * Row 4 — Learning Activity.
 */
export async function getLearningActivity() {
  return apiClient.get(LEARNING_ACTIVITY_PATH);
}

export function normalizeLearningActivity(raw) {
  return {
    totalBoxes: raw?.total_boxes ?? 0,
    completed: raw?.boxes_completed ?? 0,
    attempted: raw?.boxes_attempted ?? 0,
    activeRuns: raw?.active_runs_total ?? 0,
    runsIndividual: raw?.active_runs_individual ?? 0,
    runsTeam: raw?.active_runs_team ?? 0,
  };
}

/**
 * Row 5 — Box Library & Box Creation.
 *  */
export async function getBoxLibrary(topCreatorsLimit = 5, weeks = 8) {
  return apiClient.get(
    `${BOX_LIBRARY_PATH}?top_creators_limit=${topCreatorsLimit}&weeks=${weeks}`,
  );
}

/**
 * Normalize box-library. Creator initials/tints aren't in the payload, so
 * initials are derived from display_name and tint cycles a fixed palette.
 * successRate + funnel percentages are derived from outcomes_chart counts.
 */
export function normalizeBoxLibrary(raw) {
  const TINT_KEYS = ["primary", "blue", "violet", "orange", "teal"];

  const creators = raw?.top_creators ?? [];

  const weeklyLabels = raw?.weekly_creation_chart?.labels ?? [];
  const weeklyValues = raw?.weekly_creation_chart?.datasets?.[0]?.data ?? [];

  const outcomeLabels = raw?.outcomes_chart?.labels ?? [];
  const outcomeValues = raw?.outcomes_chart?.datasets?.[0]?.data ?? [];
  const byLabel = (name, fallbackIdx) => {
    const i = outcomeLabels.findIndex((l) => String(l).toLowerCase() === name);
    const idx = i !== -1 ? i : fallbackIdx;
    return outcomeValues[idx] ?? 0;
  };
  const successful = byLabel("successful", 0);
  const failed = byLabel("failed", 1);
  const outcomeTotal = successful + failed;
  const successRate =
    outcomeTotal > 0 ? Math.round((successful / outcomeTotal) * 100) : 0;

  return {
    owned: raw?.owned ?? 0,
    pending: raw?.pending ?? 0,
    approvedThisMonth: raw?.approved_this_month ?? 0,
    approvedByMe: raw?.approved_by_me ?? 0,

    topCreators: creators.map((c, i) => ({
      id: c.user_id ?? `${c.display_name ?? "user"}-${i}`,
      name: c.display_name ?? "Unknown",
      boxes: c.boxes_created ?? 0,
      initials: initialsFromName(c.display_name),
      tint: TINT_KEYS[i % TINT_KEYS.length],
    })),

    weekly: {
      labels: weeklyLabels,
      values: weeklyValues,
    },

    outcomes: {
      successful,
      failed,
      successRate,
      successPct: outcomeTotal > 0 ? (successful / outcomeTotal) * 100 : 0,
      failedPct: outcomeTotal > 0 ? (failed / outcomeTotal) * 100 : 0,
    },
  };
}

/**
 * Row 6 — LLM Token Usage.
 */
export async function getTokenUsage(topConsumersLimit = 5) {
  return apiClient.get(
    `${TOKEN_USAGE_PATH}?top_consumers_limit=${topConsumersLimit}`,
  );
}

/** Abbreviate a token count: 284000 -> "284K", 1500000 -> "1.5M", 847 -> "847". */
function formatTokens(n) {
  const num = Number(n) || 0;
  if (num >= 1_000_000) {
    const m = num / 1_000_000;
    return `${m >= 10 ? Math.round(m) : m.toFixed(1)}M`;
  }
  if (num >= 1000) {
    const k = num / 1000;
    return `${k >= 10 ? Math.round(k) : k.toFixed(1)}K`;
  }
  return String(Math.round(num));
}

export function normalizeTokenUsage(raw) {
  const TINT_KEYS = ["primary", "blue", "violet", "orange", "teal"];

  const labels = raw?.daily_chart?.labels ?? [];
  const series = raw?.daily_chart?.datasets?.[0]?.data ?? [];

  const nearCap = raw?.near_cap ?? [];
  const consumers = raw?.top_consumers ?? [];

  return {
    stats: {
      total: formatTokens(raw?.tokens_total_30d ?? 0),
      today: formatTokens(raw?.tokens_today ?? 0),
      week: formatTokens(raw?.tokens_this_week ?? 0),
      avgTurn: formatTokens(raw?.tokens_avg_per_turn ?? 0),
    },
    labels: labels.map((iso) => {
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString("en", { month: "short", day: "numeric" });
    }),
    series,
    nearCap: nearCap.map((m, i) => {
      const tokens = m.tokens ?? m.token_total ?? m.tokens_used ?? 0;
      const cap = m.cap ?? m.token_cap ?? m.cap_per_user ?? 0;
      const pct = cap > 0 ? Math.round((tokens / cap) * 100) : 0;
      return {
        id: m.user_id ?? `${m.display_name ?? "user"}-${i}`,
        name: m.display_name ?? "Unknown",
        tokens,
        cap,
        pct,
        initials: initialsFromName(m.display_name),
        tint: TINT_KEYS[i % TINT_KEYS.length],
      };
    }),
    topConsumers: consumers.map((c, i) => {
      const tokens = c.tokens ?? c.token_total ?? c.tokens_used ?? 0;
      return {
        id: c.user_id ?? `${c.display_name ?? "user"}-${i}`,
        name: c.display_name ?? "Unknown",
        tokens,
        value: formatTokens(tokens),
        initials: initialsFromName(c.display_name),
        tint: TINT_KEYS[i % TINT_KEYS.length],
      };
    }),
  };
}
